import { createSocketOpener } from './socket';
import { v7 } from 'uuid';
import { NetworkError, ProblemError, type HttpClient, type HttpMethod, type Problem, type RequestOptions, type RawUploadOptions, type UploadOptions } from '@/model';
import { capabilities, initialProtocol, webVersion } from './protocol';
import { bindAdmission, CapabilityAdmission } from './capabilities';

export interface HttpAuth {
  generation?(): number;
  accessToken?(): Promise<string>;
  onRetire?(handler: () => void): () => void;
  token(): string | undefined;
  refresh(): Promise<void>;
  rejected(error: ProblemError, generation?: number): Promise<void>;
}
export interface HttpOptions {
  baseUrl?: string;
  fetch?: typeof fetch;
  auth?: HttpAuth;
  protocol?: () => number;
  admission?: CapabilityAdmission;
}
export function retryAfter(value: string | null): number | undefined {
  if (!value) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  const date = Date.parse(value); return Number.isFinite(date) ? Math.max(0, Math.ceil((date - Date.now()) / 1000)) : undefined;
}
async function responseError(response: Response): Promise<Error> {
  if (response.headers.get('Content-Type')?.includes('application/problem+json')) {
    try { const p = await response.json() as Problem; if (typeof p.code === 'string') return new ProblemError({ ...p, status: response.status }, retryAfter(response.headers.get('Retry-After'))); } catch { /* Treat malformed/non-problem bodies as transport failures. */ }
  }
  return new NetworkError('The server did not return a usable response');
}

export interface ChunkHttpClient extends HttpClient {
  uploadChunk<T>(path: string, chunk: Blob, first: number, total: number, options?: UploadOptions): Promise<T>;
}

export function createHttpClient(options: HttpOptions = {}): ChunkHttpClient {
  const fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
  const base = options.baseUrl ?? globalThis.location?.origin ?? 'http://localhost';
  const admission = options.admission ?? new CapabilityAdmission();
  const discardResult = async (value: unknown, opts?: RequestOptions) => {
    try { await opts?.onDiscardedResult?.(value); }
    catch { /* Retirement cleanup is best effort and never exposes the discarded result. */ }
  };
  let refreshFlight: Promise<void> | undefined;
  let refreshGeneration: number | undefined;
  const refresh = async (rejectedToken: string | undefined) => {
    if (!options.auth) return;
    if (options.auth.token() !== rejectedToken) return;
    const generation = options.auth.generation?.();
    if (!refreshFlight || refreshGeneration !== generation) {
      refreshGeneration = generation;
      refreshFlight = options.auth.refresh().catch(async error => { if (generation === options.auth?.generation?.() && error instanceof ProblemError && (error.status === 401 && error.code !== 'invalid_credentials' || error.code === 'account_suspended')) await options.auth?.rejected(error, generation); throw error; }).finally(() => { if (refreshGeneration === generation) refreshFlight = undefined; });
    }
    await refreshFlight;
  };
  const url = (path: string, query?: RequestOptions['query']) => {
    const value = new URL(path, base);
    if (value.origin !== new URL(base).origin) throw new NetworkError('Request origin is outside this account');
    for (const [key, item] of Object.entries(query ?? {})) if (item !== undefined && item !== null) value.searchParams.set(key, String(item));
    return value;
  };
  const headers = (path: string, opts?: RequestOptions) => {
    const result = new Headers(opts?.headers);
    if (!result.has('Accept')) result.set('Accept', 'application/json');
    result.delete('Authorization');
    result.set('X-Hylo-Protocol', String(opts?.protocol ?? options.protocol?.() ?? initialProtocol));
    result.set('X-Hylo-Client', webVersion.client);
    result.set('X-Hylo-Capabilities', [...new Set([...capabilities, ...(opts?.capabilities ?? []), ...(result.get('X-Hylo-Capabilities') ?? '').split(',').map(v => v.trim()).filter(Boolean)])].join(','));
    result.set('X-Request-Id', v7());
    if (/^\/v1\/projects\/[^/]+\/files(?:\/|$)/.test(path)) result.set('X-Hylo-Files-Mode', 'folder');
    if (opts?.cause) result.set('X-Hylo-Fetch-Cause', opts.cause);
    const token = !opts?.anonymous ? options.auth?.token() : undefined;
    if (token) result.set('Authorization', `Bearer ${token}`);
    return result;
  };
  const send = async (method: HttpMethod, path: string, opts: RequestOptions = {}): Promise<Response> => {
    const generation = options.auth?.generation?.();
    const admissionEpoch = admission.capture();
    for (let attempt = 0; attempt < 2; attempt++) {
      if (!opts.anonymous && generation !== options.auth?.generation?.()) throw new NetworkError('This account session has ended');
      const h = headers(path, opts); const usedToken = opts.anonymous ? undefined : options.auth?.token();
      if (opts.body !== undefined) h.set('Content-Type', 'application/json');
      let response: Response;
      try { response = await fetcher(url(path, opts.query), { method, headers: h, body: opts.body === undefined ? undefined : JSON.stringify(opts.body), signal: opts.signal ? AbortSignal.any([opts.signal, AbortSignal.timeout(opts.timeoutMs ?? 30000)]) : AbortSignal.timeout(opts.timeoutMs ?? 30000), credentials: 'omit', cache: 'no-store', redirect: 'error' }); }
      catch { throw new NetworkError('Unable to reach Hylo'); }
      if (!opts.anonymous && generation !== options.auth?.generation?.()) {
        if (response.ok && opts.onDiscardedResult) {
          try { await discardResult(await response.json(), opts); }
          catch { /* A malformed response contains no usable cleanup result. */ }
        }
        throw new NetworkError('This account session has ended');
      }
      if (response.ok || response.status === 304 && method === 'GET' && (h.has('If-None-Match') || h.has('If-Modified-Since'))) { if (!opts.anonymous) admission.http(response.headers.get('X-Hylo-Capabilities'), admissionEpoch); return response; }
      const error = await responseError(response);
      if (!opts.anonymous && generation !== options.auth?.generation?.()) throw new NetworkError('This account session has ended');
      if (error instanceof ProblemError) {
        if (error.status === 401 && error.code !== 'invalid_credentials' && !opts.anonymous && attempt === 0 && options.auth) { await refresh(usedToken); if (generation !== options.auth?.generation?.()) throw new NetworkError('This account session has ended'); continue; }
        if (!opts.anonymous && (error.status === 401 && error.code !== 'invalid_credentials' || error.code === 'account_suspended')) await options.auth?.rejected(error, generation);
      }
      throw error;
    }
    throw new NetworkError('Authentication retry did not complete');
  };
  const request = async <T>(method: HttpMethod, path: string, opts?: RequestOptions): Promise<T> => {
    const generation = options.auth?.generation?.();
    const response = await send(method, path, opts);
    if (response.status === 204 || response.status === 304) return undefined as T;
    try {
      const value = await response.json() as T;
      if (!opts?.anonymous && generation !== options.auth?.generation?.()) {
        await discardResult(value, opts);
        throw new NetworkError('This account session has ended');
      }
      return value;
    } catch { throw new NetworkError('Invalid JSON response'); }
  };
  const uploadOnce = <T>(path: string, form: FormData | Blob, opts: UploadOptions & Pick<RawUploadOptions, 'headers' | 'query'>, method = 'POST', extraHeaders: Record<string, string> = {}): Promise<T> => new Promise((resolve, reject) => {
    const generation = options.auth?.generation?.();
    const admissionEpoch = admission.capture();
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    if (opts.signal?.aborted) { reject(new NetworkError('Upload cancelled')); return; }
    xhr.open(method, url(path, opts.query));
    for (const [key, value] of headers(path, { headers: { ...opts.headers, ...extraHeaders } })) xhr.setRequestHeader(key, value);
    xhr.withCredentials = false;
    xhr.timeout = 120000;
    xhr.upload.onprogress = event => { if (generation === options.auth?.generation?.() && !opts.signal?.aborted) opts.onProgress?.(event.loaded, event.lengthComputable ? event.total : 0); };
    const finish = () => { stopRetire?.(); opts.signal?.removeEventListener('abort', abort); };
    xhr.onerror = xhr.ontimeout = () => { finish(); reject(new NetworkError('Upload could not reach Hylo')); };
    xhr.onabort = () => { finish(); reject(new NetworkError('Upload cancelled')); };
    xhr.onload = () => {
      finish();
      if (generation !== options.auth?.generation?.()) { reject(new NetworkError('This account session has ended')); return; }
      if (xhr.status >= 200 && xhr.status < 300) { admission.http(xhr.getResponseHeader('X-Hylo-Capabilities'), admissionEpoch); try { resolve((xhr.status === 204 ? undefined : JSON.parse(xhr.responseText)) as T); } catch { reject(new NetworkError('Invalid upload response')); } return; }
      if (xhr.getResponseHeader('Content-Type')?.includes('application/problem+json')) {
        try { reject(new ProblemError({ ...JSON.parse(xhr.responseText) as Problem, status: xhr.status }, retryAfter(xhr.getResponseHeader('Retry-After')))); return; } catch { /* Invalid problem. */ }
      }
      reject(new NetworkError('Upload failed without a usable answer'));
    };
    const stopRetire = options.auth?.onRetire?.(abort);
    opts.signal?.addEventListener('abort', abort, { once: true }); xhr.send(form);
  });
  const uploadAuthenticated = async <T>(path: string, form: FormData | Blob, opts: UploadOptions & Pick<RawUploadOptions, 'headers' | 'query'> = {}, method = 'POST', extraHeaders: Record<string, string> = {}): Promise<T> => {
      const generation = options.auth?.generation?.();
      const usedToken = options.auth?.token();
      try { return await uploadOnce<T>(path, form, opts, method, extraHeaders); }
      catch (error) {
        if (generation !== options.auth?.generation?.()) throw new NetworkError('This account session has ended');
        if (error instanceof ProblemError && error.status === 401 && options.auth) {
          await refresh(usedToken);
          if (generation !== options.auth?.generation?.()) throw new NetworkError('This account session has ended');
          try { return await uploadOnce<T>(path, form, opts, method, extraHeaders); } catch (retryError) { if (generation !== options.auth?.generation?.()) throw new NetworkError('This account session has ended'); if (retryError instanceof ProblemError && (retryError.status === 401 || retryError.code === 'account_suspended')) await options.auth.rejected(retryError, generation); throw retryError; }
        }
        if (error instanceof ProblemError && error.code === 'account_suspended') await options.auth?.rejected(error, generation);
        throw error;
      }
  };
  const response = async (method: HttpMethod, path: string, opts?: RequestOptions) => {
    const generation = options.auth?.generation?.();
    const result = await send(method, path, opts);
    // Some browser/proxy paths expose an empty stream on a null-body status. Rewrapping it is forbidden.
    if ([204, 205, 304].includes(result.status) || !result.body || opts?.anonymous) return result;
    const reader = result.body.getReader();
    const retire = options.auth?.onRetire?.(() => { void reader.cancel().catch(() => undefined); });
    const body = new ReadableStream<Uint8Array>({
      async pull(controller) {
        try {
          if (opts?.signal?.aborted || generation !== options.auth?.generation?.()) throw new NetworkError('This account session has ended');
          const chunk = await reader.read();
          if (opts?.signal?.aborted || generation !== options.auth?.generation?.()) throw new NetworkError('This account session has ended');
          if (chunk.done) { retire?.(); controller.close(); } else controller.enqueue(chunk.value);
        } catch (error) { retire?.(); void reader.cancel().catch(() => undefined); controller.error(error); }
      },
      cancel: reason => { retire?.(); return reader.cancel(reason); },
    }, { highWaterMark: 0 });
    return new Response(body, { status: result.status, statusText: result.statusText, headers: result.headers });
  };
  const client: ChunkHttpClient = {
    response, openWebSocket: createSocketOpener(base, options.auth, options.protocol),
    uploadRaw: (path, bytes, opts) => uploadAuthenticated(path, bytes, opts, opts.method),
    request, get: (path, opts) => request('GET', path, opts), post: (path, body, opts) => request('POST', path, { ...opts, body }), put: (path, body, opts) => request('PUT', path, { ...opts, body }), patch: (path, body, opts) => request('PATCH', path, { ...opts, body }), delete: (path, opts) => request('DELETE', path, opts),
    blob: async (path, opts) => { const generation = options.auth?.generation?.(); const response = await send('GET', path, opts); try { const value = await response.blob(); if (!opts?.anonymous && generation !== options.auth?.generation?.()) throw new NetworkError('This account session has ended'); return value; } catch { throw new NetworkError('Unable to read media'); } },
    upload: uploadAuthenticated,
    uploadChunk: (path, chunk, first, total, opts) => uploadAuthenticated(path, chunk, opts, 'PUT', { 'Content-Type': 'application/octet-stream', 'Content-Range': `bytes ${first}-${first + chunk.size - 1}/${total}` }),
  };
  bindAdmission(client, admission); return client;
}
